/**
 * Premium Selection ↔ Quote adapter.
 *
 * Maps the section's form state (manual inputs + FE-computed totals +
 * criteria checks + approval dates/notes) to the real backend Quote
 * (`/v2/submissions/{id}/quote/`, PUT/GET).
 *
 * Two conventions the wire enforces:
 *   • Rates are stored as FRACTIONS ("0.150000" = 15%); the FE edits whole
 *     percents ("15"). We ÷100 on write, ×100 on read.
 *   • The 40% / Referral criteria are enum-member string arrays; the FE
 *     criteria carry their own ids, so an explicit id↔enum map bridges
 *     them (see CHECK MAPS).
 */
import type { PremiumSelectionDerived, PremiumSelectionInputs } from "../../domain/pricing";
import type { MiscFeeWire } from "../../types/misc-fee";
import { formDateToIso, isoDateToForm } from "./submission";

// ─────────────────────────── Wire types ───────────────────────────

/** GET `/quote/` — every money/rate field comes back as a decimal string. */
export interface SubmissionQuoteGetWire {
    submission_id: string;
    /** Σ maturity_adjusted_premium (renamed from `primary_premium`, OR-1119).
     *  Shown as "Premium w/ Retro-date Credit" / "Primary Premium". */
    premium_with_retro_date_credit: string | null;
    /** Σ sir_adjusted_premium (new, OR-1119). "Premium w/ SIR Credit". */
    premium_with_sir_credit: string | null;
    /** Backend-computed across all four walk steps (OR-1514), in sheet order:
     *  premium_with_sir_credit × (1 − base_rate_credit_factor)
     *   × (1 − quality_credit_factor) × (1 − experience_credit_factor)
     *   × (1 − corridor_credit). Every step is a rate bounded [0,1] since
     *  OR-1540, so no floor is needed. A null at any step is a no-op, not a
     *  zeroed premium. */
    adjusted_premium: string | null;
    total_premium: string | null;
    selected_premium: string | null;
    /** System-suggested premium — the premium at the target loss ratio.
     *  Feeds the "Suggested Premium" chip next to Selected Premium. */
    premium_at_target_loss_ratio: string | null;
    final_premium: string | null;
    total_commission_rate: string | null;
    tria_fee_rate: string | null;
    tria_fee_amount: string | null;
    surplus_lines_tax_amount: string | null;
    surplus_lines_fee_amount: string | null;
    stamping_fee_amount: string | null;
    /** RATE (decimal fraction) InsCipher filed the Stamping Fee against
     *  (OR-1440 follow-up) — same contract as `surplus_lines_tax_rate`. */
    stamping_fee_rate: string | null;
    /** Extra fees (OR-1339) — flat dollar adds picked from the "+Add Fee"
     *  modal's predefined list. `null` = not added / removed. */
    mcare_fee: string | null;
    indiana_pcf_surcharge: string | null;
    louisiana_pcf_surcharge: string | null;
    /** Loss ratio over the utilized-years window (renamed from
     *  `selected_loss_ratio`, backend PR #303): full_loss_excess_sir /
     *  (selected_premium × utilized_loss_years). */
    full_loss_ratio: string | null;
    /** Loss ratio over the most-recent 5 report years (backend PR #303). */
    capped_loss_ratio_5yr: string | null;
    deviation_from_unity: string | null;
    /** OR-1321 renamed `years_of_utilized_loss_runs`. Underwriter-selectable
     *  loss-ratio denominator, resolved server-side: defaults to
     *  min(5, recorded_loss_years), capped at recorded_loss_years. */
    utilized_loss_years: number | null;
    /** Data-derived distinct count of dated loss-run report years — the
     *  maximum selectable utilized_loss_years (OR-1321). */
    recorded_loss_years: number | null;
    /** True when fewer than 5 loss years are on record, so the underwriter
     *  should confirm the defaulted utilized-years selection (OR-1321). */
    utilized_loss_years_requires_confirmation: boolean | null;
    /**
     * FIRST step of the premium walk (OR-1514). RATE — decimal fraction,
     * bounded [0,1] by the wire, same shape as quality/corridor.
     *
     * NAME: `base_rate_credit_factor`, not `base_rate_credit`. The frontend
     * integration doc calls it the latter; what shipped and what dev returns
     * is the `_factor` suffix (verified against the deployed GET, sep-10).
     * Getting this wrong fails silently in BOTH directions — the GET key reads
     * undefined and the PUT key is dropped as unknown.
     *
     * `null` on a quote written before OR-1514 (no backfill, deliberately);
     * `0.15` seeded server-side the moment a new quote row is created, which
     * the page never sends and the user never chose.
     */
    base_rate_credit_factor: string | null;
    quality_credit_factor: string | null;
    /** THIRD step of the walk. RATE — decimal fraction bounded [0,1], same
     *  shape as base rate / quality / corridor. Was a dollar amount
     *  (`experience_credit`) until OR-1540 replaced it: only the dollars were
     *  persisted, so the percentage re-derived itself whenever the premium
     *  under it moved. No default; null on quotes nobody has set it on. */
    experience_credit_factor: string | null;
    sir_credit: string | null;
    /** RATE (decimal fraction, 0.02 = 2%) since OR-1315 — was dollars. */
    corridor_credit: string | null;
    tech_rm_fee: string | null;
    /**
     * Who owns `tech_rm_fee` (backend PR #371). `false`: backend-derived — covered
     * facilities × $5,000, recomputed on every quote write and location
     * change, so archiving a facility moves it. `true`: a person set it (a PUT
     * that sent a CHANGED `tech_rm_fee`, or a pricing-model import with a
     * value in C33) and location changes no longer restate it. Read-only:
     * the PUT does not accept it — sending `tech_rm_fee` is what flips it,
     * and an explicit `null` flips it back.
     *
     * OPTIONAL: absent on environments that predate the backend change;
     * treat missing as "unknown", not as `false`.
     */
    tech_rm_fee_manually_set?: boolean | null;
    surplus_lines_tax_rate: string | null;
    surplus_lines_fee_rate: string | null;
    /**
     * True when the underwriter hand-typed this rate rather than InsCipher
     * calculating it — the backend's own provenance signal (vs. the FE's
     * session-local "does the live % differ from what we last loaded"
     * heuristic, which can't tell "backend recalculated correctly" apart
     * from "backend forgot to recalculate" after a reload).
     *
     * OPTIONAL: not confirmed shipped on every environment yet. Undefined/
     * absent must be treated as "unknown", NOT as `false` — callers fall
     * back to the session-local heuristic in that case, never assume
     * authored.
     */
    surplus_lines_tax_manually_set?: boolean | null;
    surplus_lines_fee_manually_set?: boolean | null;
    /** Stamping Fee counterpart of the two flags above — same "optional,
     *  absent means unknown" contract. */
    stamping_fee_rate_manually_set?: boolean | null;
    /**
     * When InsCipher last ran for this submission; null if it never has
     * (OR-1440, backend PR #327). Read-only — it is never sent on the PUT.
     *
     * OPTIONAL because that PR is not merged yet: dev omits the key entirely,
     * so typing it as required would be a lie. Drop the `?` once it ships.
     *
     * Careful with the semantics: this is "when the calculation ran", NOT "when
     * the values on screen were produced". Change the premium without
     * recalculating and it goes stale.
     */
    tax_calculated_at?: string | null;
    /**
     * Home state InsCipher resolved and filed against (OR-1440), persisted
     * alongside `tax_calculated_at` so a reload can look `hasTaxableFees` up
     * via `GET /v2/states/{code}` instead of reverse-solving it. OPTIONAL:
     * absent for a quote that's never been calculated, and possibly absent
     * on a quote calculated before this column shipped — treat missing as
     * "unknown", not as a state code to look up.
     */
    tax_state?: string | null;
    tricura_commission_rate: string | null;
    agent_commission_rate: string | null;
    punitive_damages: string | null;
    defense_in_addition: string | null;
    sexual_physical_abuse: string | null;
    hnoa: string | null;
    declared_sir: string | null;
    per_occurrence_limit: string | null;
    policy_aggregate_limit: string | null;
    // Coverage B / C dec sub-limits (OR-1455). Optional on the wire until
    // orion-backend PR #326 deploys — until then the GET simply omits them
    // and the local overlay fills in.
    fire_damage_per_claim_limit?: string | null;
    fire_damage_aggregate_limit?: string | null;
    gl_each_claim_deductible?: string | null;
    medical_expense_limit?: string | null;
    evacuation_limit?: string | null;
    evacuation_aggregate_limit?: string | null;
    evacuation_deductible?: string | null;
    referral_date: string | null;
    approval_date: string | null;
    notes: string | null;
    quality_credit_checks: string[] | null;
    referral_checks: string[] | null;
    /**
     * Underwriter-defined fee lines (OR-1494 / backend OR-1421), embedded
     * read-only so the page renders in one call. Managed through
     * `/quote/misc-fees/` — anything sent under this key on the PUT is
     * ignored. OPTIONAL: absent on environments that predate PR #356; treat
     * missing as an empty list.
     */
    misc_fees?: MiscFeeWire[] | null;
    created_at: string;
    updated_at: string;
}

/** PUT `/quote/` — amounts/rates accept numbers; dates/notes strings.
 *  The backend recomputes the premium-walk totals (adjusted/total premium,
 *  etc.) from its real upstream data — the FE never writes those. A few
 *  figures are the opposite: FE-computed and persisted as provided, never
 *  recomputed backend-side (final_premium, deviation_from_unity,
 *  total_commission_rate, tria_fee_amount, and surplus_lines_*_amount when
 *  manually overridden) — see each field's own comment below. The PUT is a
 *  merge — absent fields are left unchanged. */
export interface SubmissionQuotePutWire {
    /**
     * OPTIONAL and OMITTED WHEN EMPTY (OR-1514) — the key must be ABSENT, not
     * null. The backend merges the PUT over the stored row with
     * `exclude_unset`, and on the very first save (no quote row yet) it merges
     * over its own defaults, where this is seeded to 0.15. An explicit null
     * would therefore WIPE the seeded credit on the save that creates the row.
     * Absent leaves whatever the server decided.
     */
    base_rate_credit_factor?: number | null;
    quality_credit_factor: number | null;
    /** RATE (decimal fraction) since OR-1540 — sent ÷100 from the % input.
     *  Always sent: there is no seeded default to protect (unlike the base
     *  rate above), so an explicit null is the only way to clear one the
     *  underwriter removed. */
    experience_credit_factor: number | null;
    sir_credit: number | null;
    /** Underwriter-selected loss-years window — the full_loss_ratio
     *  denominator. Omitted/null → the backend defaults it to the
     *  recorded-year span (backend PR #303). */
    utilized_loss_years: number | null;
    /** RATE (decimal fraction) since OR-1315 — sent ÷100 from the % input. */
    corridor_credit: number | null;
    /**
     * OPTIONAL — sent only when the underwriter changed the fee on this page
     * (backend PR #371, see `ToWireQuoteOptions.includeTechRmFee`). The backend flips
     * `tech_rm_fee_manually_set` on a CHANGED value and, from then on, stops
     * deriving the fee from the covered facility count; an explicit `null`
     * hands it back to the derivation. Echoing the stored value back is
     * harmless (the flag is gated on a change, not on presence), but the key
     * is still withheld on routine autosaves so the page never looks like it
     * owns a number it didn't type. Never sent at all between OR-1501 and
     * backend PR #371, when the backend overwrote whatever arrived here.
     */
    tech_rm_fee?: number | null;
    /**
     * OPTIONAL since OR-1440: these columns are InsCipher-authored. The quote
     * PUT merges (`exclude_unset`), so omitting the keys leaves the calculated
     * rates alone — they are only sent when the underwriter overrides a rate by
     * hand. Sending them on every autosave used to overwrite InsCipher's values
     * with whatever sat in the % input, and an empty input sent an explicit null.
     */
    surplus_lines_tax_rate?: number | null;
    surplus_lines_fee_rate?: number | null;
    /**
     * FE-computed `selected × rate%` (the same figure the card already shows
     * live), sent alongside the rate ONLY on a manual override — once the
     * underwriter hand-types a rate there's no InsCipher amount to defer to,
     * so the FE's own number is the one to persist. Never sent otherwise, for
     * the same reason as the rate: it would stomp InsCipher's filed amount.
     */
    surplus_lines_tax_amount?: number | null;
    surplus_lines_fee_amount?: number | null;
    /** Stamping Fee counterpart of the surplus-lines rate/amount pair above —
     *  same "OPTIONAL, sent only on a manual override" contract. */
    stamping_fee_rate?: number | null;
    stamping_fee_amount?: number | null;
    /** Extra fees (OR-1339) — always sent (value when added, `null` when
     *  removed/never added); no InsCipher-authorship conflict to gate on,
     *  unlike the surplus-lines fields above. */
    mcare_fee: number | null;
    indiana_pcf_surcharge: number | null;
    louisiana_pcf_surcharge: number | null;
    tria_fee_rate: number | null;
    /** FE-computed (Selected Premium × 5% × tria_fee_rate, OR-1513) and
     *  persisted as provided — same contract as deviation_from_unity, never
     *  recomputed backend-side. Always sent (unlike the surplus-lines
     *  amounts, TRIA has no InsCipher-authored counterpart to defer to). */
    tria_fee_amount: number | null;
    tricura_commission_rate: number | null;
    agent_commission_rate: number | null;
    /** FE-computed (tricura + agent) and persisted as provided — the
     *  backend never sums it (same contract as deviation_from_unity). */
    total_commission_rate: number | null;
    punitive_damages: number | null;
    defense_in_addition: number | null;
    sexual_physical_abuse: number | null;
    hnoa: number | null;
    selected_premium: number | null;
    final_premium: number | null;
    // Frontend-computed rate, sent so it persists + populates the Triage
    // "Deviation from Market" column (backend stores as provided, never
    // recomputes — see orion-backend docs/terminology.md).
    deviation_from_unity: number | null;
    declared_sir: number | null;
    per_occurrence_limit: number | null;
    policy_aggregate_limit: number | null;
    // Coverage B / C dec sub-limits (OR-1455). Sent once the backend
    // accepts them; harmless before that (Pydantic drops unknown keys).
    fire_damage_per_claim_limit: number | null;
    fire_damage_aggregate_limit: number | null;
    gl_each_claim_deductible: number | null;
    medical_expense_limit: number | null;
    evacuation_limit: number | null;
    evacuation_aggregate_limit: number | null;
    evacuation_deductible: number | null;
    referral_date: string | null;
    approval_date: string | null;
    notes: string | null;
    quality_credit_checks: string[];
    referral_checks: string[];
}

// ─────────────────────────── Helpers ──────────────────────────────

function numOrNull(raw: string): number | null {
    // Defensive: a wire field the backend dropped in a contract change
    // arrives as `undefined` — treat it like empty instead of crashing the
    // whole hydration (this is exactly how the other_fee removal broke the
    // page on dev before OR-1319).
    if (raw == null || raw.trim() === "") return null;
    const n = Number(raw.replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
}

/** Integer input string → int (utilized_loss_years is a bare count). */
function intOrNull(raw: string): number | null {
    const n = numOrNull(raw);
    return n === null ? null : Math.round(n);
}

/** Fit a computed rate to the wire's Numeric(8,6): round to 6 decimal
 *  places (raw float math carries 15+ digits and 422s past 8 total) AND
 *  clamp magnitude to ±99.999999 (2 integer digits max — an extreme
 *  Selected Premium can push the ratio past it). Null-preserving. */
function roundRate6(n: number | null): number | null {
    if (n === null) return null;
    const clamped = Math.min(99.999999, Math.max(-99.999999, n));
    return Number(clamped.toFixed(6));
}

/** Whole-percent string ("15") → fraction (0.15), rounded to the wire's
 *  Numeric(8,6) — ÷100 float math can exceed the backend's 8-digit cap
 *  (422 decimal_max_digits). "" → null. */
function pctToFraction(raw: string): number | null {
    const n = numOrNull(raw);
    return n === null ? null : Number((n / 100).toFixed(6));
}

/** Sum two wire fractions, null only when BOTH are absent — the
 *  FE-computed total_commission_rate (tricura + agent, per its tooltip).
 *  Rounded to the wire's Numeric(8,6). */
function sumFractions(a: number | null, b: number | null): number | null {
    if (a === null && b === null) return null;
    return Number(((a ?? 0) + (b ?? 0)).toFixed(6));
}

/** Fit a dollar amount to the wire's Numeric(14,2): round to cents (FE
 *  money is float math with long tails) AND clamp to 12 integer digits —
 *  a max-range Selected Premium plus fees pushes final_premium past the
 *  cap and the PUT 422s. */
function roundMoney2(n: number | null): number | null {
    if (n === null) return null;
    const clamped = Math.min(999999999999.99, Math.max(0, n));
    return Number(clamped.toFixed(2));
}

/** Fraction string ("0.150000") → whole-percent input ("15"). */
/** Wire rate (decimal fraction) → the whole-percent string the % inputs hold.
 *  Exported for OR-1440: after an InsCipher calculation the page hydrates the
 *  rate inputs from the response, and it has to use the same conversion the
 *  quote hydration does or the two would disagree on rounding. */
export function fractionToPctInput(raw: string | null): string {
    const n = raw === null ? null : numOrNull(raw);
    return n === null ? "" : String(Number((n * 100).toFixed(6)));
}

/** Decimal money string ("50000.00") → canonical FE input ("50000"). */
function moneyToInput(raw: string | null): string {
    const n = raw === null ? null : numOrNull(raw);
    return n === null ? "" : String(n);
}

/** Decimal string ("125000.00") → number, preserving null (the UI shows
 *  "—" for null via `displayMoneyValue` / `displayPercent*`). */
function decimalOrNull(raw: string | null): number | null {
    return raw === null ? null : numOrNull(raw);
}

/** Fraction string ("0.25") → whole-percent number (25), preserving null.
 *  The wire stores commission as a fraction; the UI's `displayPctNumber`
 *  expects a whole percent. */
function fractionToWholePct(raw: string | null): number | null {
    const n = decimalOrNull(raw);
    return n === null ? null : Number((n * 100).toFixed(6));
}

// ─────────────────────────── Check maps ───────────────────────────

/** FE 40% criterion id → backend QualityCreditCheck enum member. */
const FORTY_PCT_TO_ENUM: Record<string, string> = {
    "above-surveys": "above_state_natl_surveys",
    "top-25-metrics": "top_25_metrics",
    "good-aesthetic": "good_aesthetic",
    "good-social": "good_social_activities",
    "no-jeopardy-tags": "no_immediate_jeopardy_tags_2yr",
    "loss-ratio-15": "loss_ratio_15_5yr_data",
    "loss-ratio-25-deductible": "loss_ratio_25_5yr_adjusted_deductible",
    "no-sff-abuse": "no_sff_abuse_claims_1yr",
    "no-100k-claims": "max_2_100k_claims_5yr",
};

/** FE referral criterion id → backend ReferralCheck enum member. */
const REFERRAL_TO_ENUM: Record<string, string> = {
    "premiums-above-1m": "premiums_above_1m",
    "incurred-lr-40": "loss_ratio_5yr_over_40",
    "single-claim-500k": "single_claim_3yr_over_500k",
    "deductible-above-250k": "deductible_above_250k",
    "sexual-assault-claims": "sexual_assault_claims_with_indemnity",
    "renewal-lr-over-50": "renewal_loss_ratio_over_50_accelerant",
    "premium-adjustment-higher-lr": "premium_adjustment_post_renewal_higher_lr",
    "new-corridor-buffer": "new_corridor_or_buffer_policy",
};

/** Build enum→firstFeId reverse lookups (first FE id wins for dup enums). */
function reverse(map: Record<string, string>): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [feId, enumVal] of Object.entries(map)) {
        if (!(enumVal in out)) out[enumVal] = feId;
    }
    return out;
}
const ENUM_TO_FORTY_PCT = reverse(FORTY_PCT_TO_ENUM);
const ENUM_TO_REFERRAL = reverse(REFERRAL_TO_ENUM);

/** Checked FE ids (Record<id,bool>) → sorted enum member array. */
function checksToEnums(checks: Record<string, boolean>, map: Record<string, string>): string[] {
    const set = new Set<string>();
    for (const [feId, on] of Object.entries(checks)) {
        if (on && map[feId]) set.add(map[feId]);
    }
    return Array.from(set);
}

/** Enum member array → FE check record (Record<feId,bool>). */
function enumsToChecks(enums: string[] | null, reverseMap: Record<string, string>): Record<string, boolean> {
    const out: Record<string, boolean> = {};
    for (const e of enums ?? []) {
        const feId = reverseMap[e];
        if (feId) out[feId] = true;
    }
    return out;
}

// ─────────────────────────── Adapters ─────────────────────────────

export interface QuoteFormState {
    inputs: PremiumSelectionInputs;
    fortyPctCriteria: Record<string, boolean>;
    referralCriteria: Record<string, boolean>;
    referralDate: string;
    approvalDate: string;
    approvalNotes: string;
    /** FE-computed Final Premium. Sent on save so a policy can be created
     *  (the backend requires the quote's `final_premium` to be set). */
    finalPremium: number | null;
    /** FE-computed Deviation from Unity. Sent on save so it persists and
     *  the Triage "Deviation from Market" column is populated. */
    deviationFromUnity: number | null;
    /** FE-computed TRIA dollar amount — the same value `computeFees` derives
     *  for the on-screen card. Always sent on save (unlike the surplus-lines
     *  amounts, there's no InsCipher-authored figure to defer to), so the
     *  persisted amount tracks the % input instead of staying frozen at
     *  whatever was last written (OR ticket: `tria_fee_amount` was never
     *  sent at all, so it never moved off a stale seed value). */
    triaFeeAmount: number | null;
    /**
     * Live `selected × rate%` for surplus lines tax/fee — the same values
     * `computeFees` derives for the on-screen card. Only reaches the wire
     * when the matching rate is a manual override (see
     * `ToWireQuoteOptions.includeSurplusLinesRates`); otherwise dropped so a
     * routine autosave can't stomp InsCipher's filed amount.
     */
    surplusLinesTaxAmount: number | null;
    surplusLinesFeeAmount: number | null;
    /** Stamping Fee counterpart of the two above — same round-trip contract. */
    stampingFeeAmount: number | null;
}

export interface ToWireQuoteOptions {
    /**
     * Send the surplus-lines tax rate + amount columns (OR-1440). Defaults to
     * false: they are InsCipher-authored, so a routine autosave must leave them
     * untouched. Pass true only when the underwriter has typed this rate by
     * hand — the design keeps manual entry, and that is the one case the FE
     * owns the value.
     *
     * Independent of `includeSurplusLinesFee`: the underwriter may override
     * only one of the pair, and forcing both to travel together would resend
     * the untouched field's rate/amount on every such save — risking
     * overwriting a fresher InsCipher value with a stale locally-cached one.
     */
    includeSurplusLinesTax?: boolean;
    /** Fee counterpart of `includeSurplusLinesTax` — see there for the contract. */
    includeSurplusLinesFee?: boolean;
    /** Stamping Fee counterpart of `includeSurplusLinesTax`/`includeSurplusLinesFee`
     *  — same independent-gating contract, since all three fields can be
     *  overridden separately. */
    includeStampingFee?: boolean;
    /**
     * Send `tech_rm_fee` (backend PR #371). Defaults to false: the fee is
     * backend-derived from the covered facility count unless a person set it,
     * and a routine autosave must not look like that person. Pass true only
     * when the value on screen differs from what the quote last handed us —
     * the underwriter typed a fee, or cleared the field. An empty input goes
     * out as an explicit `null`, which is the backend's "back to automatic"
     * signal: the flag drops and the fee resumes tracking the schedule.
     */
    includeTechRmFee?: boolean;
}

/** Form state → PUT body (manual inputs + criteria/dates/notes only). */
export function toWireQuote(state: QuoteFormState, options?: ToWireQuoteOptions): SubmissionQuotePutWire {
    const i = state.inputs;
    return {
        // OMITTED (key absent) while the input is empty — never sent as null.
        // On the save that CREATES the quote row the backend merges this PUT
        // over its defaults, where base_rate_credit_factor is seeded to 0.15;
        // an explicit null would wipe that seed and quietly re-price the
        // submission ~15% higher than the sheet. Same absent-not-null contract
        // as the surplus-lines rates below, for a different reason.
        ...(i.baseRateCreditPct.trim() === "" ? {} : { base_rate_credit_factor: pctToFraction(i.baseRateCreditPct) }),
        quality_credit_factor: pctToFraction(i.qualityCreditPct),
        // A rate like its neighbours (OR-1540). Always sent, unlike the base
        // rate above: nothing is seeded here, so null is the legitimate way
        // to clear it.
        experience_credit_factor: pctToFraction(i.experienceCreditPct),
        // The premium-walk SIR maps to `declared_sir` (Henry, jun-19), not
        // `sir_credit` (which is the backend-computed retention credit, left
        // null here — the FE has no credit figure to write).
        sir_credit: null,
        utilized_loss_years: intOrNull(i.utilizedLossYears),
        corridor_credit: pctToFraction(i.corridorCreditPct),
        // Omitted unless the user overrode that field's rate — see
        // ToWireQuoteOptions. The keys must be ABSENT, not null: the backend
        // merges with `exclude_unset`, so an explicit null would still wipe
        // the column. Tax and fee are gated independently so overriding one
        // can't force-resend the other's untouched rate/amount. The amount
        // rides along with its rate (same condition) — it's the live figure
        // already on screen, not a recompute here.
        ...(options?.includeSurplusLinesTax
            ? {
                  surplus_lines_tax_rate: pctToFraction(i.surplusLinesTaxPct),
                  surplus_lines_tax_amount: roundMoney2(state.surplusLinesTaxAmount),
              }
            : {}),
        ...(options?.includeSurplusLinesFee
            ? {
                  surplus_lines_fee_rate: pctToFraction(i.surplusLinesFeePct),
                  surplus_lines_fee_amount: roundMoney2(state.surplusLinesFeeAmount),
              }
            : {}),
        ...(options?.includeStampingFee
            ? {
                  stamping_fee_rate: pctToFraction(i.stampingFeePct),
                  stamping_fee_amount: roundMoney2(state.stampingFeeAmount),
              }
            : {}),
        // Same absent-vs-null distinction as the InsCipher trio (backend PR #371): the
        // key travels only when the underwriter changed the fee, and an empty
        // input becomes an explicit null on purpose — that is how the backend
        // is told to derive the fee again. See ToWireQuoteOptions.
        ...(options?.includeTechRmFee ? { tech_rm_fee: numOrNull(i.techRmFee) } : {}),
        mcare_fee: numOrNull(i.mcareFee),
        indiana_pcf_surcharge: numOrNull(i.indianaPcfSurcharge),
        louisiana_pcf_surcharge: numOrNull(i.louisianaPcfSurcharge),
        tria_fee_rate: pctToFraction(i.triaPct),
        tria_fee_amount: roundMoney2(state.triaFeeAmount),
        tricura_commission_rate: pctToFraction(i.tricuraCommissionPct),
        agent_commission_rate: pctToFraction(i.agentCommissionPct),
        total_commission_rate: sumFractions(pctToFraction(i.tricuraCommissionPct), pctToFraction(i.agentCommissionPct)),
        punitive_damages: numOrNull(i.punitiveDamages),
        defense_in_addition: numOrNull(i.defenseInAddition),
        sexual_physical_abuse: numOrNull(i.abuse),
        hnoa: numOrNull(i.hnoa),
        selected_premium: numOrNull(i.selectedPremium),
        final_premium: roundMoney2(state.finalPremium),
        deviation_from_unity: roundRate6(state.deviationFromUnity),
        declared_sir: numOrNull(i.sir),
        per_occurrence_limit: numOrNull(i.perOccurrenceLimit),
        policy_aggregate_limit: numOrNull(i.policyAggregateLimit),
        // Coverage B / C dec sub-limits (OR-1455).
        fire_damage_per_claim_limit: numOrNull(i.fireDamagePerClaimLimit),
        fire_damage_aggregate_limit: numOrNull(i.fireDamageAggregateLimit),
        gl_each_claim_deductible: numOrNull(i.glEachClaimDeductible),
        medical_expense_limit: numOrNull(i.medicalExpenseLimit),
        evacuation_limit: numOrNull(i.evacuationLimit),
        evacuation_aggregate_limit: numOrNull(i.evacuationAggregateLimit),
        evacuation_deductible: numOrNull(i.evacuationDeductible),
        referral_date: formDateToIso(state.referralDate),
        approval_date: formDateToIso(state.approvalDate),
        notes: state.approvalNotes.trim() === "" ? null : state.approvalNotes,
        quality_credit_checks: checksToEnums(state.fortyPctCriteria, FORTY_PCT_TO_ENUM),
        referral_checks: checksToEnums(state.referralCriteria, REFERRAL_TO_ENUM),
    };
}

/**
 * GET body → derived totals. As of jun-19 every total on the Premium
 * Selection section is BACKEND-COMPUTED: the quote endpoint recomputes
 * them on PUT and returns them on GET, so the FE no longer sums facilities
 * / loss-runs. This reads them straight off the wire.
 *
 * Row → wire mapping (OR-1119, jun-22 — backend renamed/added fields):
 *   "Primary Premium"                ← premium_with_retro_date_credit
 *   "Premium w/ Retro-date Credit"   ← premium_with_retro_date_credit
 *   "Premium w/ SIR Credit"          ← premium_with_sir_credit   (new)
 *   "Base Rate Credit"               ← base_rate_credit_factor (fraction → %, OR-1514)
 *   "Experience Credit/Debit"        ← experience_credit_factor (fraction → %, OR-1540)
 *   "Adjusted Premium"               ← adjusted_premium          (backend-computed)
 *   "Total Premium"                  ← total_premium             (backend-computed)
 *   "Total Commission"               ← total_commission_rate (fraction → %)
 *   "TRIA"                           ← tria_fee_amount        ($ amount)
 *   "Surplus Lines Tax"              ← surplus_lines_tax_amount
 *   "Surplus Lines Fee"              ← surplus_lines_fee_amount
 *   "Stamping Fee"                   ← stamping_fee_amount
 *   "Final Premium"                  ← final_premium
 *   "Selected Loss Ratio"            ← selected_loss_ratio  (0–1 fraction)
 *   "Deviation from Unity"           ← deviation_from_unity (0–1 fraction)
 */
export function derivedFromWireQuote(wire: SubmissionQuoteGetWire): PremiumSelectionDerived {
    return {
        // Σ maturity_adjusted_premium (was primary_premium). Same value feeds
        // the "Primary Premium" card and the "Premium w/ Retro-date Credit" row.
        primaryPremium: decimalOrNull(wire.premium_with_retro_date_credit),
        retroDateCreditPremium: decimalOrNull(wire.premium_with_retro_date_credit),
        // Σ sir_adjusted_premium — new "Premium w/ SIR Credit" row.
        premiumWithSirCredit: decimalOrNull(wire.premium_with_sir_credit),
        // Backend-computed now (was FE-computed off the walk): the backend
        // owns adjusted_premium / total_premium — read them straight off.
        adjustedPremium: decimalOrNull(wire.adjusted_premium),
        totalPremium: decimalOrNull(wire.total_premium),
        premiumAtTargetLossRatio: decimalOrNull(wire.premium_at_target_loss_ratio),
        // total_commission_rate arrives as a fraction (0.25); the UI row wants
        // a whole percent (25). TODO(backend): confirm it's truly a fraction.
        totalCommissionPct: fractionToWholePct(wire.total_commission_rate),
        tria: decimalOrNull(wire.tria_fee_amount),
        surplusLinesTax: decimalOrNull(wire.surplus_lines_tax_amount),
        surplusLinesFee: decimalOrNull(wire.surplus_lines_fee_amount),
        stampingFee: decimalOrNull(wire.stamping_fee_amount),
        finalPremium: decimalOrNull(wire.final_premium),
        // Loss ratios / deviation arrive as 0–1 fractions; the UI's
        // displayPercent multiplies by 100.
        fullLossRatio: decimalOrNull(wire.full_loss_ratio),
        cappedLossRatio5yr: decimalOrNull(wire.capped_loss_ratio_5yr),
        deviationFromUnity: decimalOrNull(wire.deviation_from_unity),
    };
}

/** GET body → form state (defaults preserved by the caller for absent keys). */
export function fromWireQuote(wire: SubmissionQuoteGetWire, defaults: PremiumSelectionInputs): QuoteFormState {
    return {
        inputs: {
            ...defaults,
            // SIR round-trips through `declared_sir` now (Henry, jun-19).
            sir: moneyToInput(wire.declared_sir),
            // Rate → whole percent, same as quality/corridor. A new quote
            // arrives carrying the server-seeded 0.15, so this is what puts
            // "15" in the field without the page ever choosing it (OR-1514).
            baseRateCreditPct: fractionToPctInput(wire.base_rate_credit_factor),
            qualityCreditPct: fractionToPctInput(wire.quality_credit_factor),
            // A rate since OR-1540 (was dollars) — same fraction → % as above.
            experienceCreditPct: fractionToPctInput(wire.experience_credit_factor),
            corridorCreditPct: fractionToPctInput(wire.corridor_credit),
            punitiveDamages: moneyToInput(wire.punitive_damages),
            defenseInAddition: moneyToInput(wire.defense_in_addition),
            abuse: moneyToInput(wire.sexual_physical_abuse),
            hnoa: moneyToInput(wire.hnoa),
            tricuraCommissionPct: fractionToPctInput(wire.tricura_commission_rate),
            agentCommissionPct: fractionToPctInput(wire.agent_commission_rate),
            selectedPremium: moneyToInput(wire.selected_premium),
            techRmFee: moneyToInput(wire.tech_rm_fee),
            triaPct: fractionToPctInput(wire.tria_fee_rate),
            surplusLinesTaxPct: fractionToPctInput(wire.surplus_lines_tax_rate),
            surplusLinesFeePct: fractionToPctInput(wire.surplus_lines_fee_rate),
            stampingFeePct: fractionToPctInput(wire.stamping_fee_rate),
            mcareFee: moneyToInput(wire.mcare_fee),
            indianaPcfSurcharge: moneyToInput(wire.indiana_pcf_surcharge),
            louisianaPcfSurcharge: moneyToInput(wire.louisiana_pcf_surcharge),
            perOccurrenceLimit: moneyToInput(wire.per_occurrence_limit),
            policyAggregateLimit: moneyToInput(wire.policy_aggregate_limit),
            // Coverage B / C dec sub-limits (OR-1455). Absent from the wire
            // until PR #326 deploys — `moneyToInput(undefined)` yields "",
            // so the caller's defaults (and the local overlay) stand in.
            fireDamagePerClaimLimit: moneyToInput(wire.fire_damage_per_claim_limit ?? null) || defaults.fireDamagePerClaimLimit,
            fireDamageAggregateLimit: moneyToInput(wire.fire_damage_aggregate_limit ?? null) || defaults.fireDamageAggregateLimit,
            glEachClaimDeductible: moneyToInput(wire.gl_each_claim_deductible ?? null) || defaults.glEachClaimDeductible,
            medicalExpenseLimit: moneyToInput(wire.medical_expense_limit ?? null) || defaults.medicalExpenseLimit,
            evacuationLimit: moneyToInput(wire.evacuation_limit ?? null) || defaults.evacuationLimit,
            evacuationAggregateLimit: moneyToInput(wire.evacuation_aggregate_limit ?? null) || defaults.evacuationAggregateLimit,
            evacuationDeductible: moneyToInput(wire.evacuation_deductible ?? null) || defaults.evacuationDeductible,
            utilizedLossYears: wire.utilized_loss_years == null ? "" : String(wire.utilized_loss_years),
        },
        fortyPctCriteria: enumsToChecks(wire.quality_credit_checks, ENUM_TO_FORTY_PCT),
        referralCriteria: enumsToChecks(wire.referral_checks, ENUM_TO_REFERRAL),
        referralDate: isoDateToForm(wire.referral_date),
        approvalDate: isoDateToForm(wire.approval_date),
        approvalNotes: wire.notes ?? "",
        finalPremium: numOrNull(wire.final_premium ?? ""),
        // Persisted value (may be null from older saves); the page recomputes it
        // live from Selected Premium + Total Premium regardless.
        deviationFromUnity: decimalOrNull(wire.deviation_from_unity),
        // Round-trip of the persisted amount; the page overwrites this with
        // its own live figure (liveFees) before every save.
        surplusLinesTaxAmount: numOrNull(wire.surplus_lines_tax_amount ?? ""),
        surplusLinesFeeAmount: numOrNull(wire.surplus_lines_fee_amount ?? ""),
        // Same round-trip contract as the two above.
        stampingFeeAmount: numOrNull(wire.stamping_fee_amount ?? ""),
        // Same round-trip contract as the two above — the page overwrites
        // this with its own live figure (liveFees.tria) before every save.
        triaFeeAmount: numOrNull(wire.tria_fee_amount ?? ""),
    };
}
